<template>
  <div
    class="process-order"
    :class="{ 'process-order--plain': variant === 'plain' }"
  >
    <div class="loading" v-if="isLoading"></div>
    <template v-else>
      <button
        type="button"
        :class="[
          'process-order-cta',
          variant === 'plain' ? 'process-order-cta--plain' : 'add-to-cart-btn',
        ]"
        @click.prevent="(event) => processOrderAction(event)"
        v-html="processordertext ? processordertext : $t('labels.processOrder')"
      ></button>
      <p
        v-if="showDisclaimer"
        class="cta-disclaimer"
        v-html="disclaimerText"
      ></p>
      <template v-if="!hideSkip">
        <span class="or-separator">{{ $t("labels.or") }}</span>
        <button
          type="button"
          class="skip-process-btn"
          @click.prevent="(event) => skipProcessAction(event)"
        >
          {{ $t("labels.skipProcess") }}
        </button>
      </template>
    </template>
  </div>
</template>

<script>
import CartService from "@/services/cart";
import { useDraftStore } from "@/store/draft";
import ProductService from "@/services/product";
import { useOrderHistoryStore } from "@/store/orderHistory";
import Orion from "@/services/orion";
import { useCartStore } from "@/store/cart";
import Utils from "@/services/utils";
import Navigation from "@/services/navigation";
import { constants } from "@/constants";
import PageLoader from "@/services/pageLoader";
import DuplicateOrderGuard from "@/services/duplicateOrderGuard";
import Redirect from "@/services/redirect";
import { resolvePreviousOrderForExtrasell } from "@/services/confirmationPreview";
import { markPreviewExtrasellConsumed } from "@/services/extrasellPayload";

export default {
  name: "CheckoutButtonProcessOrder",
  props: {
    item: {
      type: Object,
    },
    type: {
      type: String,
      default: "crosssell",
    },
    processordertext: {
      type: String,
    },
    step: {
      type: Number,
      default: 0,
    },
    hideSkip: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: "default",
      validator: (v) => ["default", "plain"].includes(v),
    },
    // Legal disclaimer rendered between the CTA button and the skip link.
    // - `undefined` (default) falls back to i18n `labels.ctaDisclaimer`
    //   ONLY for the "plain" (rich content) variant, so legacy usages
    //   keep their current UI intact.
    // - An empty string explicitly hides the disclaimer.
    // - A non-empty string overrides the i18n default (HTML allowed).
    disclaimer: {
      type: String,
      default: undefined,
    },
  },
  computed: {
    isLoading() {
      return useDraftStore().isSendingDraftOrder;
    },
    disclaimerText() {
      if (this.disclaimer !== undefined) return this.disclaimer;
      if (this.variant === "plain") return this.$t("labels.ctaDisclaimer");
      return "";
    },
    showDisclaimer() {
      return Boolean(this.disclaimerText);
    },
  },
  methods: {
    async processOrderAction(event) {
      if (!this.item) {
        return;
      }

      this.item.qtyIncr = 1;
      this.item.choice = ProductService.getDefaultChoice(this.item);
      CartService.addToCart(this.item);

      useDraftStore().sendingDraftOrder(true);
      try {
        const orderHistoryStore = useOrderHistoryStore();
        const previousOrder = resolvePreviousOrderForExtrasell({
          currentPageLastOrder: orderHistoryStore.lastOrderHistoryItem?.order,
          orderHistory: orderHistoryStore.orderHistory,
          paramPlusOrderId: Redirect.getParamPlusData()?.orderId || null,
        });
        if (!previousOrder) {
          return;
        }

        const newOrder = Object.assign({}, previousOrder);
        newOrder.previousExternalOrderId = previousOrder.externalOrderId;
        newOrder.externalOrderId = Utils.generateUID();

        if (
          (this.type === "crosssell" || this.type === "upsell") &&
          useDraftStore().initialOrderData?.paymentType ===
            constants.PAYMENT_METHOD_BANK_CARD
        ) {
          newOrder.initialExternalOrderId =
            useDraftStore().initialOrderData?.externalOrderId;
          newOrder.isPaymentCaptured = true;
        }

        useDraftStore().updateOrder(newOrder);

        const preparedOrder = Orion.prepareOrder(
          useCartStore().cart,
          newOrder,
          { orderType: this.type },
        );
        if (!preparedOrder.testLevel && previousOrder.testLevel) {
          preparedOrder.testLevel = previousOrder.testLevel;
        }
        useDraftStore().updateOrder(preparedOrder);
        console.log("ProcessOrderPreparedOrder", preparedOrder);

        const minifiedCart = CartService.minifyCart();
        minifiedCart.items.map(
          (item) =>
            (item.choice.configUrl = `${window.location.origin}/data/products/${item.choice.productId}.json`),
        );

        const blocked = await DuplicateOrderGuard.blockIfDuplicate(
          useCartStore().cart,
          preparedOrder,
          { orderType: this.type },
        );
        if (blocked) {
          return;
        }

        await Orion.sendOrder(
          minifiedCart,
          preparedOrder,
          window.globalConfig.domainId,
          this.type,
        );

        orderHistoryStore.addOrderToHistory(
          useCartStore().cart,
          preparedOrder,
          this.step,
        );
        markPreviewExtrasellConsumed(window?.globalConfig?.domainId);

        useDraftStore().draftOrderSent(true);

        CartService.emptyCart();

        const paramPlus = this.buildParamPlus(preparedOrder);
        localStorage.setItem(constants.PARAM_PLUS, JSON.stringify(paramPlus));

        PageLoader.show(this.$t("messages.processingOrder"));
        window.location.href = `${window.location.origin}${window.globalConfig.waitingPage}`;
      } catch (error) {
        console.error(error);
      } finally {
        useDraftStore().sendingDraftOrder(false);
      }
    },
    buildParamPlus(preparedOrder) {
      const paramPlus = {};

      // FORGE-308: parsing robuste via URLSearchParams (gère valeurs avec `=`,
      // encodage URL, clés sans valeur). Filtrage des params preview/test pour
      // ne pas polluer le flow paiement (cf. constants.IGNORED_PARAM_PLUS_KEYS).
      const queryParams = Navigation.getQueryParams();
      for (const [key, value] of queryParams) {
        if (!constants.IGNORED_PARAM_PLUS_KEYS.includes(key)) {
          paramPlus[key] = value;
        }
      }

      paramPlus.orderId = preparedOrder.externalOrderId;
      paramPlus.paymentType = preparedOrder.paymentType;
      // FORGE-308: MIT cross-sell/upsell → action "recapture" pour bypasser le
      // polling Ogone sur la waiting page (pas d'autorisation Ogone à poller)
      paramPlus.action =
        this.type === "crosssell" || this.type === "upsell"
          ? "recapture"
          : "capture";
      paramPlus.authorizationType = "capture_now";
      paramPlus.retryUrl = `${window.location.origin}${window.location.pathname.replace(/\/$/, "")}?retry=${preparedOrder.externalOrderId}`;

      // Keep vanguard_only on the confirmation return so extrasell preview
      // continues after an accepted upsell (stripped from the waiting URL).
      if (preparedOrder.testLevel === "vanguard_only") {
        paramPlus.testLevel = preparedOrder.testLevel;
      }

      return paramPlus;
    },
    skipProcessAction(event) {
      CartService.addToCartLoading(true);

      markPreviewExtrasellConsumed(window?.globalConfig?.domainId);
      useOrderHistoryStore().addStepToHistory(this.step);

      CartService.addToCartLoading(false);
    },
  },
};
</script>

<style scoped>
/* Upsell / cross-sell CTA — bigger and more prominent */
.process-order-cta {
  padding: 0.85rem 1.5rem;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: 6px;
  transition:
    transform 0.1s ease,
    box-shadow 0.1s ease;
}
.process-order-cta.add-to-cart-btn {
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.process-order-cta:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
}

/* Plain variant — FORGE-264 rich content mode
   The CTA styling is fully controlled by the span inside Argument1__c
   (produced by the Froala editor). We strip the default button chrome
   so the marketer's design is rendered as-is. */
.process-order--plain {
  text-align: center;
  margin: 0.5rem 0;
}
.process-order-cta--plain {
  background: transparent;
  border: 0;
  padding: 0;
  width: auto;
  box-shadow: none;
  cursor: pointer;
}
.process-order-cta--plain:hover:not(:disabled) {
  transform: none;
  box-shadow: none;
}

/* Legal disclaimer below every CTA (CGV mention). Small, muted, italic —
   sits between the button and the "ou / Passer cette étape" skip link. */
.cta-disclaimer {
  font-size: 0.75rem;
  color: #666;
  font-style: italic;
  margin: 0.5rem auto 0.25rem;
  max-width: 32rem;
  line-height: 1.35;
}
</style>
